import { capitalizeFirstLetter } from '@/lib/stringUtil';
import formatDate from '../date-time/utils';

export const COMPONENT_TYPES = {
  SELECT: 'select',
  HORIZONTAL_REPEATER: 'horizontal-repeater',
  VERTICAL_REPEATER: 'vertical-repeater',
  DATE: 'date',
  TEXT_AREA: 'text-area',
  LABEL: 'label',
  MESSAGE: 'message'
};

export interface Component {
  uiType: string;
  name: string;
  label?: string;
  value?: any;
  children?: Component[];
  option?: string;
  min?: string;
  max?: string;
  selectOptions?: { value: string; label: string }[];
}

export interface FormModel {
  [key: string]: {
    value?: any;
    label?: string;
  };
}

export interface Form {
  model: FormModel;
}

export interface RuleData {
  components: Component[];
  [key: string]: any;
}

export function getActiveFormRuleData(
  form: Form,
  components: Component[]
): Component[] | null {
  let selectComponentExists = false;
  let selection;
  let searchableComponents;
  const formKeys = Object.keys(form.model);
  for (let i = 0; i < formKeys.length; i++) {
    const formFieldName = formKeys[i];
    if (formFieldName.indexOf(COMPONENT_TYPES.SELECT) !== -1) {
      selectComponentExists = true;
      selection =
        form.model[formFieldName] && form.model[formFieldName].value
          ? form.model[formFieldName].value
          : null;
      if (selection) {
        searchableComponents = searchComponents({option: selection}, components);
        break;
      }
    }
  }
  if (!selectComponentExists) searchableComponents = components;
  if (!selection || !searchableComponents) return null;
  let foundData: Component[] = [];
  Object.keys(form.model).forEach(key => {
    const foundDataItems = searchComponents({name: key}, searchableComponents);
    if (foundDataItems) foundData = foundData.concat(foundDataItems);
  });
  return foundData;
}

export function augmentRuleData(data: RuleData): RuleData {
  if (data.components != null && data.components.length) {
    const components = augmentComponentData(data.components);
    const saved = getSavedDataCollection(components);
    return {
      ...data,
      components,
      saved,
      isDataSaved: saved.length > 0,
      savedRequestCount: saved.length,
    };
  }
  return { ...data };
}

export function getFormFromSavedData(components: Component[]): FormModel | null {
  let formMap;
  const foundSelectComponents = searchComponents({uiType: COMPONENT_TYPES.SELECT}, components);
  if (foundSelectComponents) {
    const selectComponent = foundSelectComponents[0];
    if (selectComponent.value) {
      return {
        [selectComponent.name]: {
          value: selectComponent.value,
          label: selectComponent.value
        }
      };
    }
    for (const component of selectComponent.children || []) {
      const {option} = component;
      const savedDataCollection = getSavedDataCollection(component);
      if (savedDataCollection.length) {
        formMap = buildFormMapFromSavedData(savedDataCollection);
        formMap[selectComponent.name] = {
          value: option,
          label: option
        };
        return formMap;
      }
    }
  } else {
    const savedDataCollection = getSavedDataCollection(components);
    if (savedDataCollection.length) {
      return buildFormMapFromSavedData(savedDataCollection);
    }
  }
  return null;
}

function buildFormMapFromSavedData(savedDataCollection: Component[]): FormModel {
  const formMap: FormModel = {};
  for (const savedComponent of savedDataCollection) {
    const savedValue =
      savedComponent.uiType === COMPONENT_TYPES.DATE
        ? formatDate(savedComponent.value)
        : savedComponent.value;
    formMap[savedComponent.name] = savedValue;
  }
  return formMap;
}

export function getSavedDataCollection(
  components: Component[] | Component,
  collector?: Component[]
): Component[] {
  collector = collector || [];
  const searchComponent =
    !Array.isArray(components) && (components as Component).children ? (components as Component).children! : components as Component[];
  for (const component of searchComponent) {
    if (
      component.uiType === COMPONENT_TYPES.DATE ||
      component.uiType === COMPONENT_TYPES.TEXT_AREA ||
      (component.uiType === COMPONENT_TYPES.SELECT && component.value)
    ) {
      if (component.uiType === COMPONENT_TYPES.SELECT) {
        component.value = component.value.value;
      }
      if (component.value) {
        collector.push(component);
      }
    }
    if (component.children && component.children.length) {
      getSavedDataCollection(component.children, collector);
    }
  }
  return collector;
}

export function augmentComponentData(components: Component[]): Component[] {
  return components.map(component => {
    let newComponent = { ...component };
    if (newComponent.label)
      newComponent.label = capitalizeFirstLetter(newComponent.label);
    if (newComponent.uiType === COMPONENT_TYPES.DATE) {
      newComponent = {
        ...newComponent,
        min:
          newComponent.min && newComponent.min !== '1900-01-01'
            ? formatDate(newComponent.min)
            : null,
        max: newComponent.max ? formatDate(newComponent.max) : newComponent.max,
        value: newComponent.value ? formatDate(newComponent.value) : newComponent.value,
      };
    }
    if (newComponent.uiType === COMPONENT_TYPES.SELECT) {
      const options: { value: string; label: string }[] = [];
      let selectedValue = null;
      const newChildren = (newComponent.children || []).map(child => {
        const childCopy = { ...child, option: capitalizeFirstLetter(child.option!) };
        const option = {
          value: childCopy.option!,
          label: childCopy.option!,
        };
        options.push(option);
        if (childCopy.value) selectedValue = option;
        return childCopy;
      });
      newComponent = {
        ...newComponent,
        children: newChildren,
        selectOptions: options,
        value: selectedValue,
      };
    } else if (newComponent.children && newComponent.children.length) {
      newComponent = {
        ...newComponent,
        children: augmentComponentData(newComponent.children),
      };
    }
    return newComponent;
  });
}

export function searchComponents(
  searchObject: { [key: string]: any },
  components: Component[]
): Component[] | null {
  for (const component of components) {
    const searchKeys = Object.keys(searchObject);
    let searchMatches = 0;
    Object.keys(searchObject).forEach(key => {
      const value = searchObject[key];
      if (component[key] && component[key] === value) {
        searchMatches++;
      }
    });
    if (searchMatches === searchKeys.length) {
      return [component];
    }
    if (component.children && component.children.length) {
      const foundInChildren = searchComponents(searchObject, component.children);
      if (foundInChildren) return foundInChildren;
    }
  }
  return null;
}

